<template>
  <button 
    v-if="showInstallButton" 
    @click="triggerInstall"
    class="install-button"
  >
    <span class="nav-icon">📱</span>
    <span class="install-text">{{ $t('install.button_text') }}</span>
  </button>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const showInstallButton = ref(false)

onMounted(() => {
  // Check if PWA should show install option
  checkInstallability()
  
  // Listen for PWA state changes
  window.addEventListener('appinstalled', () => {
    showInstallButton.value = false
    console.log('[InstallButton] App installed, hiding install button')
  })
  
  // Re-check installability periodically in case conditions change
  const intervalId = setInterval(() => {
    checkInstallability()
  }, 30000) // Check every 30 seconds
  
  // Clean up interval on component unmount
  onUnmounted(() => {
    clearInterval(intervalId)
  })
})

const checkInstallability = () => {
  // Check if already in PWA mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator.standalone === true)
  
  // Check if already marked as installed
  const isPwaInstalled = localStorage.getItem('pwaInstalled') === 'true'
  
  // Show button if not in PWA mode and not marked as installed
  const shouldShow = !isStandalone && !isPwaInstalled
  
  // Additional check for browser PWA support
  const hasPWASupport = isPWACapableBrowser()
  
  showInstallButton.value = shouldShow && hasPWASupport
  
  console.log('[InstallButton] Installability check:', {
    isStandalone,
    isPwaInstalled,
    hasPWASupport,
    shouldShow: showInstallButton.value
  })
}

const isPWACapableBrowser = () => {
  const ua = navigator.userAgent
  
  // Check for browsers with good PWA support
  const supportedBrowsers = {
    isChromiumBased: /Chrome|Edg|OPR|Opera/i.test(ua),
    isFirefox: /Firefox/i.test(ua),
    isIOSSafari: /iPhone|iPad|iPod/i.test(ua) && /Safari/i.test(ua)
  }
  
  return supportedBrowsers.isChromiumBased || 
         supportedBrowsers.isFirefox || 
         supportedBrowsers.isIOSSafari
}

const triggerInstall = async () => {
  console.log('[InstallButton] Install button clicked')
  
  // Try native prompt first
  if (window.PWAInstall?.deferredPrompt) {
    try {
      console.log('[InstallButton] Using native installation prompt')
      await window.PWAInstall.deferredPrompt.prompt()
      
      const { outcome } = await window.PWAInstall.deferredPrompt.userChoice
      console.log(`[InstallButton] Native install outcome: ${outcome}`)
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwaInstalled', 'true')
        showInstallButton.value = false
      }
      
      // Reset the deferred prompt
      window.PWAInstall.deferredPrompt = null
    } catch (error) {
      console.error('[InstallButton] Native installation failed:', error)
      // Fall through to fallback
      showFallbackInstructions()
    }
  } else {
    // No native prompt available, show fallback instructions
    console.log('[InstallButton] No native prompt, showing fallback instructions')
    showFallbackInstructions()
  }
}

const showFallbackInstructions = () => {
  // Use the enhanced fallback prompt from main.js if available
  if (window.PWAInstall?.showFallbackPrompt) {
    window.PWAInstall.showFallbackPrompt()
  } else {
    // Simple browser-specific instructions as fallback
    const ua = navigator.userAgent
    let instructions = ''
    
    if (/iPhone|iPad|iPod/i.test(ua)) {
      instructions = `${t('install.ios_instructions')}\n\n1. ${t('install.ios_step1')}\n2. ${t('install.ios_step2')}\n3. ${t('install.ios_step3')}`
    } else if (/Android/i.test(ua)) {
      if (/Chrome/i.test(ua)) {
        instructions = `${t('install.android_chrome_instructions')}\n\n1. ${t('install.android_chrome_step1')}\n2. ${t('install.android_chrome_step2')}`
      } else {
        instructions = `${t('install.android_instructions')}\n\n1. ${t('install.android_step1')}\n2. ${t('install.android_step2')}`
      }
    } else {
      // Desktop
      if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) {
        instructions = `${t('install.chrome_instructions')}\n\n1. ${t('install.chrome_step1')}\n2. ${t('install.chrome_step2')}`
      } else if (/Edg/i.test(ua)) {
        instructions = `${t('install.edge_instructions')}\n\n1. ${t('install.edge_step1')}\n2. ${t('install.edge_step2')}`
      } else {
        instructions = `${t('install.generic_instructions')}\n\n1. ${t('install.generic_step1')}\n2. ${t('install.generic_step2')}`
      }
    }
    
    alert(instructions)
  }
}
</script>

<style scoped>
.install-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  box-shadow: none;
}

.install-button:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: none;
  box-shadow: none;
}

.install-button:active {
  transform: none;
  box-shadow: none;
}

/* Hide emoji in desktop nav */
.nav-icon {
  display: none;
}

.install-text {
  white-space: nowrap;
}

/* Responsive design */
@media (max-width: 768px) {
  .install-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .install-icon {
    width: 14px;
    height: 14px;
  }
  
  /* Option to hide text on very small screens */
  @media (max-width: 480px) {
    .install-text {
      display: none;
    }
    
    .install-button {
      padding: 0.5rem;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      justify-content: center;
    }
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .install-button {
    background: linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%);
  }
  
  .install-button:hover {
    background: linear-gradient(135deg, #1B5E20 0%, #0d4b12 100%);
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .install-button {
    border: 2px solid currentColor;
    background: var(--primary-color, #4CAF50);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .install-button {
    transition: none;
  }
  
  .install-button:hover {
    transform: none;
  }
}
</style>